import { Component, EventEmitter, Input, OnInit, OnDestroy, Output, ChangeDetectionStrategy } from '@angular/core';
import { AppTableSource, ColumnSortType, TableColumnTypes } from '../../../../../model/table.model';
import { BehaviorSubject, combineLatest, map, Observable, Subject, take, takeUntil } from 'rxjs';
import { initUsersParams, TelegramService } from '../../../../../services/telegram.service';
import { IParams } from '../../../../../model/http';
import { NotificationService } from '../../../../../services/notifications.service';
import { environment } from '../../../../../../environments/environment';
import { IError } from '../../../../../interfaces';
import { ACTIVE_EMPLOYEES_PARAMS, DEFAULT_ERROR, ERROR_MESSAGES } from '../../../../../enum/constants';
import { DialogService } from '../../../../../services/dialog.service';
import { CrmService } from '../../../../../services/crm.service';
import { formatFullName } from '../../../../../helpers';

interface IManagerOption {
  id: string;
  name: string;
}

export const tableHeaders = [
  // {
  //   value: 'primary',
  //   sort: ColumnSortType.NONE,
  //   name: '',
  //   type: TableColumnTypes.CHECKBOX,
  //   className: 'table-checkbox'
  // },
  {
    value: 'phone_number',
    sort: ColumnSortType.NONE,
    name: 'phone',
    type: TableColumnTypes.TEXT,
    className: 'table-header',
    translate: true,
  },
  {
    value: 'tg_id',
    sort: ColumnSortType.NONE,
    name: 'approved',
    type: TableColumnTypes.BOOLEAN,
    className: 'table-header',
    translate: true,
  },
  {
    value: 'user_id',
    sort: ColumnSortType.NONE,
    name: 'assigned_user',
    type: TableColumnTypes.SELECT,
    className: 'table-header',
    disabled: true,
    translate: true,
    options: [],
    optionLabel: 'name',
    optionValue: 'id',
    placeholder: 'not_assigned',
  },
  // {
  //   value: 'notify_payment',
  //   sort: ColumnSortType.NONE,
  //   name: 'Оплата',
  //   type: TableColumnTypes.TOGGLE,
  //   className: 'table-header',
  // },
  // {
  //   value: 'notify_olx',
  //   sort: ColumnSortType.NONE,
  //   name: 'Olx',
  //   type: TableColumnTypes.BOOLEAN,
  //   className: 'table-header',
  // },
  // {
  //   value: 'notify_logistic',
  //   sort: ColumnSortType.NONE,
  //   name: 'Логістика',
  //   type: TableColumnTypes.TOGGLE,
  //   className: 'table-header',
  // },
  {
    value: 'action',
    sort: ColumnSortType.NONE,
    name: '',
    type: TableColumnTypes.ACTION,
    className: 'three-dot',
    actions: [{ icon: 'delete', label: 'delete', value: 'DELETE' }],
    translate: true,
  },
];

@Component({
  selector: 'app-integration-telegram',
  templateUrl: './integration-telegram.component.html',
  styleUrls: ['./integration-telegram.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class IntegrationTelegramComponent implements OnInit, OnDestroy {
  private readonly destroy$: Subject<null> = new Subject<null>();

  private readonly managers$ = new BehaviorSubject<IManagerOption[]>([]);

  dataSource$: Observable<AppTableSource> = combineLatest([this.telegramService.users$, this.managers$]).pipe(
    map(([data, managers]) => {
      return {
        headers: tableHeaders.map((header) => (header.value === 'user_id' ? { ...header, options: managers } : header)),
        rows: (data.items || []).map((v: any) => ({ ...v })),
        pageSize: data?.size || 0,
        pageIndex: data.page ? data.page - 1 : 0,
        length: data.total || 0,
      };
    }),
  );
  visibleColumns = tableHeaders.map((v) => v.value);

  private usersRequest: any;
  public phoneInput!: string;

  @Input() payload: any;
  @Output() close = new EventEmitter<any>();

  constructor(
    private readonly telegramService: TelegramService,
    private readonly notificationService: NotificationService,
    private readonly dialogService: DialogService,
    private readonly crmService: CrmService,
  ) {}

  getUsersWithUpdatedParams(params: IParams) {
    if (this.usersRequest) {
      this.usersRequest.unsubscribe();
    }
    this.usersRequest = this.telegramService
      .setQueryUsersParams(params)
      .pipe(takeUntil(this.destroy$), take(1))
      .subscribe();
  }

  ngOnInit() {
    this.telegramService.loadUsers().pipe(takeUntil(this.destroy$), take(1)).subscribe();

    this.loadManagers();
  }

  private loadManagers() {
    this.crmService
      .getAllManagers({ page: 1, take: 1000, ...ACTIVE_EMPLOYEES_PARAMS })
      .pipe(takeUntil(this.destroy$), take(1))
      .subscribe({
        next: (res: any) => {
          this.managers$.next(
            (res?.items || []).map((v: any) => ({
              id: v.id,
              name: formatFullName(v.first_name, v.last_name) || v.email,
            })),
          );
        },
        error: () => {
          this.managers$.next([]);
        },
      });
  }

  onAssignUser(event: { row: any; value: string | null }) {
    this.telegramService
      .assignUser(event.row.id, event.value || null)
      .pipe(takeUntil(this.destroy$), take(1))
      .subscribe({
        next: () => {
          this.getUsersWithUpdatedParams({ ...this.telegramService.queryUsersParams });
        },
        error: (res: any) => {
          (res.error?.errors || []).forEach((err: IError) => {
            this.notificationService.showError('', ERROR_MESSAGES[err.code] || DEFAULT_ERROR);
          });
          this.getUsersWithUpdatedParams({ ...this.telegramService.queryUsersParams });
        },
      });
  }

  ngOnDestroy() {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  onSortChange(e: any) {
    this.getUsersWithUpdatedParams({
      ...initUsersParams,
      sort: e.active,
      order: e.direction,
    });
  }
  onPaginationChange(paginationDetails: any) {
    const { pageIndex, pageSize } = paginationDetails;
    const page = pageIndex + 1;
    const take = pageSize;
    this.getUsersWithUpdatedParams({ page, take });
  }
  onActionChange(event: any) {
    const dialogRef = this.dialogService.openConfirm({
      text: 'dialogs.integration-telegram',
    });
    dialogRef.subscribe((result) => {
      if (result) {
        this.telegramService
          .removePhone(event.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.getUsersWithUpdatedParams({
                ...initUsersParams,
              });
            },
          });
      }
    });
  }

  addPhone() {
    this.telegramService
      .addPhone(this.phoneInput)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.getUsersWithUpdatedParams({
            ...initUsersParams,
          });
          this.phoneInput = '';
        },
        error: (res: any) => {
          (res.error?.errors || []).forEach((err: IError) => {
            if (err.code === 'MAX_INTEGRATION') {
              this.notificationService.showError('', ERROR_MESSAGES[err.code].replace('{0}', 'Telegram'));
            } else {
              this.notificationService.showError('', ERROR_MESSAGES[err.code]);
            }
          });
        },
      });
  }

  protected readonly environment = environment;
}
